/**
 * カスタムフォント管理タブ (Phase 29.4)
 * - .ttf / .otf / .woff / .woff2 をアップロード
 * - 一覧表示 + 削除 + プレビュー
 */
import { Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  useCustomFonts,
  useDeleteCustomFont,
  useUploadCustomFont,
  type CustomFontRecord,
} from '@/hooks/useCustomFonts';

export function CustomFontsTab() {
  const { data: fonts = [], isLoading } = useCustomFonts();
  const upload = useUploadCustomFont();
  const remove = useDeleteCustomFont();

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [family, setFamily] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onPickFile = (f: File | null) => {
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
    if (f && !family) setFamily(f.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9]/g, ''));
  };

  const onSubmit = async () => {
    if (!file) {
      toast.error('ファイルを選択してください');
      return;
    }
    if (!name.trim() || !family.trim()) {
      toast.error('表示名と font-family を入力してください');
      return;
    }
    try {
      await upload.mutateAsync({ file, name: name.trim(), family: family.trim() });
      toast.success('フォントをアップロードしました');
      setFile(null);
      setName('');
      setFamily('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error((e as Error).message ?? 'アップロードに失敗しました');
    }
  };

  const onDelete = async (f: CustomFontRecord) => {
    if (!window.confirm(`フォント「${f.name}」を削除しますか？`)) return;
    try {
      await remove.mutateAsync(f);
      toast.success('フォントを削除しました');
    } catch (e) {
      toast.error((e as Error).message ?? '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-6 pt-4">
      <section>
        <h3 className="text-sm font-semibold mb-2">新規アップロード</h3>
        <p className="text-xs text-[var(--text-muted)] mb-3">
          対応形式: .ttf / .otf / .woff / .woff2（最大 5MB）
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">フォントファイル</Label>
            <Input
              ref={fileRef}
              type="file"
              accept=".ttf,.otf,.woff,.woff2"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <Label className="text-xs">表示名</Label>
            <Input
              placeholder="源ノ角ゴ Variable"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">font-family（半角英数）</Label>
            <Input
              placeholder="MyBrandFont"
              value={family}
              onChange={(e) => setFamily(e.target.value)}
            />
          </div>
        </div>
        <Button
          className="mt-3"
          onClick={onSubmit}
          disabled={upload.isPending || !file}
        >
          {upload.isPending ? (
            <Loader2 className="size-4 mr-1 animate-spin" />
          ) : (
            <Upload className="size-4 mr-1" />
          )}
          アップロード
        </Button>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-2">登録済みフォント</h3>
        {isLoading ? (
          <p className="text-xs text-[var(--text-muted)]">読み込み中…</p>
        ) : fonts.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)]">登録済みのフォントはありません</p>
        ) : (
          <ul className="space-y-2">
            {fonts.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 border border-[var(--border)] rounded px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div
                    className="text-base"
                    style={{ fontFamily: `"${f.family}", sans-serif` }}
                  >
                    {f.name} — あいうえお ABCabc 123
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] truncate">
                    family: {f.family} / format: {f.format} / size: {(f.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(f)}
                  disabled={remove.isPending}
                  aria-label="delete"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
